import React, { Fragment, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog, Transition } from '@headlessui/react';
import { QRCodeSVG } from 'qrcode.react';
import {
  Link as LinkIconLucide,
  ClipboardCopy,
  Check,
  ArrowDownToLine,
  Share2,
} from 'lucide-react';
import {
  LinkIcon,
  ClipboardDocumentIcon,
  QrCodeIcon,
  CheckIcon,
  UserGroupIcon,
  BriefcaseIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import ShareReferralPanel from './ShareReferralPanel';
import ShareKitQuickBar from './ShareKitQuickBar';
import InviteContactsButton from './InviteContactsButton';
import { useLanguage } from '../../contexts/LanguageContext';
import { copyTextToClipboard } from '../../utils/clipboard';
import { referralUrlForLink } from '../../utils/referralLinks';
import Button from '../ui/Button';

const themeStyles = {
  clients: {
    gradient: 'from-orange-500 to-amber-500',
    border: 'border-orange-200/60',
    qrFg: '#c2410c',
    Icon: UserGroupIcon,
  },
  partners: {
    gradient: 'from-violet-600 to-indigo-600',
    border: 'border-violet-200/60',
    qrFg: '#5b21b6',
    Icon: BriefcaseIcon,
  },
};

function ProgramReferralCard({ programKey, link, shareMessage }) {
  const { t, formatMessage } = useLanguage();
  const styles = themeStyles[programKey] || themeStyles.clients;
  const Icon = styles.Icon;
  const theme = {
    ...styles,
    label: programKey === 'partners' ? t.enrola.programPartnersLabel : t.enrola.programClientsLabel,
    subtitle: programKey === 'partners' ? t.enrola.partnerRecruitmentSubtitle : t.enrola.newClientsSubtitle,
  };
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);

  if (!link?.link_code && !link?.code) {
    return null;
  }

  const code = link.link_code || link.code;
  const fullUrl = referralUrlForLink(link);

  const copy = async () => {
    try {
      await copyTextToClipboard(fullUrl);
      setCopied(true);
      toast.success(t.enrola.linkCopied);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t.enrola.copyFailed);
    }
  };

  const defaultMsg =
    shareMessage ||
    formatMessage(
      programKey === 'partners' ? t.enrola.defaultSharePartners : t.enrola.defaultShareClients,
      { url: fullUrl }
    );

  const downloadQRCode = () => {
    const svg = document.getElementById(`qr-${programKey}-${code}`);
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `referral-qr-${code}.png`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success(t.enrola.qrDownloaded);
      });
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className={`card-premium overflow-hidden border-2 ${theme.border}`}
      >
        <div className={`relative p-6 bg-gradient-to-br ${theme.gradient} text-white`}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="icon-tile h-14 w-14 from-white/25 to-white/10 ring-2 ring-white/30">
                <Icon className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold">{theme.label}</h3>
                <p className="text-sm text-white/90">{theme.subtitle}</p>
                <p className="mt-1 font-mono text-sm bg-black/10 inline-block px-2 py-0.5 rounded-lg">
                  {code}
                </p>
              </div>
            </div>
            <LinkIcon className="w-8 h-8 text-white/80 shrink-0" />
          </div>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-secondary-700  mb-2">{t.enrola.customLinkLabel}</label>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch">
              <input
                readOnly
                value={fullUrl}
                className="input-premium min-w-0 flex-1 py-2.5 text-xs font-mono text-secondary-800 "
                onFocus={(e) => e.target.select()}
              />
              <div className="flex gap-2">
                <motion.button
                  type="button"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={copy}
                  className={`inline-flex flex-1 items-center justify-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold transition-colors sm:flex-none ${
                    copied
                      ? 'border-success-300 bg-success-50 text-success-700'
                      : 'border-secondary-200 bg-white text-secondary-800 hover:border-orange-200'
                  }`}
                >
                  {copied ? (
                    <>
                      <Check className="h-4 w-4" strokeWidth={2.5} />
                      <span className="hidden sm:inline">{t.enrola.copiedShort}</span>
                    </>
                  ) : (
                    <>
                      <ClipboardCopy className="h-4 w-4" />
                      <span className="hidden sm:inline">{t.enrola.copyShort}</span>
                    </>
                  )}
                </motion.button>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <span className="text-xs font-medium text-secondary-500">{t.enrola.shareOn}</span>
              <ShareKitQuickBar url={fullUrl} message={defaultMsg} />
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
            <InviteContactsButton message={defaultMsg} className="sm:flex-1 sm:min-w-[200px]" />
            <motion.button
              type="button"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setShareModalOpen(true)}
              className="inline-flex min-h-[44px] w-full flex-1 items-center justify-center gap-2 rounded-xl border border-orange-200/80 bg-gradient-to-r from-orange-500/10 to-amber-500/10 px-4 py-2.5 text-sm font-semibold text-orange-800 sm:min-w-[200px] sm:w-auto"
            >
              <Share2 className="h-4 w-4" />
              {t.enrola.shareKitLabel}
            </motion.button>
          </div>

          <div className="pt-2 border-t border-white/20">
            <Button
              variant="ghost"
              onClick={() => setShowQR(!showQR)}
              icon={QrCodeIcon}
              className="w-full"
            >
              {showQR ? t.enrola.hideQR : t.enrola.showQR}
            </Button>
            <AnimatePresence>
              {showQR && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-4 flex flex-col items-center gap-3"
                >
                  <div className="rounded-2xl border border-secondary-100  bg-white  p-4 shadow-soft">
                    <QRCodeSVG
                      id={`qr-${programKey}-${code}`}
                      value={fullUrl}
                      size={192}
                      level="H"
                      includeMargin
                      bgColor="#ffffff"
                      fgColor={theme.qrFg}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="primary"
                    size="small"
                    icon={ArrowDownToLine}
                    onClick={downloadQRCode}
                  >
                    {t.enrola.downloadQR}
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>

      <Transition appear show={shareModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[100]" onClose={setShareModalOpen}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-secondary-900/80 backdrop-blur-md" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto safe-x">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="modal-sheet w-full max-h-[92dvh] transform overflow-hidden overflow-y-auto border border-white/50 bg-white/80 p-4 shadow-2xl backdrop-blur-xl sm:max-h-[90vh] sm:p-6  ">
                  <div className="mb-4 flex items-start justify-between gap-3 max-lg:flex-col">
                    <div className="min-w-0">
                      <Dialog.Title className="flex items-center gap-2 text-lg font-bold text-secondary-900 ">
                        <LinkIconLucide className="h-5 w-5 shrink-0 text-orange-500" />
                        {formatMessage(t.enrola.invitationTitle, { label: theme.label })}
                      </Dialog.Title>
                      <p className="mt-1 text-sm text-secondary-600">{t.enrola.shareModalHint}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShareModalOpen(false)}
                      className="max-lg:touch-target shrink-0 self-end rounded-xl p-2 text-secondary-500 hover:bg-secondary-100"
                      aria-label={t.common.close}
                    >
                      ×
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="text-xs font-semibold uppercase text-secondary-500">{t.enrola.yourLink}</label>
                      <div className="mt-1 flex flex-col gap-2 max-lg:gap-2 lg:flex-row lg:items-center">
                        <input readOnly value={fullUrl} className="min-w-0 flex-1 rounded-xl border border-secondary-200 bg-secondary-50/90 px-3 py-2 text-xs font-mono" />
                        <motion.button
                          type="button"
                          whileTap={{ scale: 0.97 }}
                          onClick={copy}
                          className={`max-lg:touch-target shrink-0 self-end rounded-xl border px-3 py-2 lg:self-auto ${copied ? 'border-success-300 bg-success-50' : 'border-secondary-200 bg-white'}`}
                        >
                          {copied ? <CheckIcon className="h-5 w-5 text-success-600" /> : <ClipboardDocumentIcon className="h-5 w-5" />}
                        </motion.button>
                      </div>
                    </div>
                    <ShareKitQuickBar url={fullUrl} message={defaultMsg} className="justify-center" />
                    <ShareReferralPanel url={fullUrl} message={defaultMsg} showCopyAll />
                    <div className="flex justify-center rounded-2xl border border-secondary-100  bg-white  p-4">
                      <QRCodeSVG
                        value={fullUrl}
                        size={160}
                        level="H"
                        includeMargin
                        bgColor="#ffffff"
                        fgColor={theme.qrFg}
                      />
                    </div>
                    <p className="text-center text-xs text-secondary-500">{t.enrola.scanToOpen}</p>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}

export default ProgramReferralCard;
